import { isEqual } from 'lodash-es';

import { insertInArrayByIndex } from '@editor2/services/helpersService';
import { type CourseState } from '@editor2/types/courseState';
import { ChangesStatus } from '@editor2/types/changesStatus';
import { chapterToChapterProperties } from '@editor2/mappers/chapter';

import {
	addStatus,
	deleteProperty,
	isEqualIdsArrays,
	removeStatus,
	renumberChapterPages
} from './helpers';
import { deletePageFromChapterHelper } from './pagesAdapter';
import { deletePageGroupFromChapterHelper } from './pageGroupsAdapter';

export const addChapterHelper = (state: CourseState, payload) => {
	const { chapterId, chapterEntity } = payload;
	const index = chapterId ? state.chapters.ids.indexOf(chapterId) + 1 : 0;
	const chapterIdsList = state.chapters.ids;

	state.chapters.entities[chapterEntity.family_id] = chapterEntity;
	state.chapters.ids = insertInArrayByIndex(chapterIdsList, chapterEntity.family_id, index);

	state.changes.chapters[chapterEntity.family_id] = [ChangesStatus.New];
	state.changes.toc = addStatus(state.changes.toc, ChangesStatus.ChildrenReorder);

	state.chapters.ids.map((chapterId) =>
		renumberChapterPages(state, state.chapters.entities[chapterId])
	);

	return state;
};

export const moveChapterHelper = (state: CourseState, payload) => {
	const { chapterId, index } = payload;

	const initialChapterIds = state.initialCourse.chapters.ids;
	const chapterIdsFiltered = state.chapters.ids.filter((id) => id !== chapterId);
	const chapterIdsUpdated = insertInArrayByIndex(chapterIdsFiltered, chapterId, index);

	state.chapters.ids = chapterIdsUpdated;
	state.changes.toc = isEqualIdsArrays(initialChapterIds, chapterIdsUpdated)
		? removeStatus(state.changes.toc, ChangesStatus.ChildrenReorder)
		: addStatus(state.changes.toc, ChangesStatus.ChildrenReorder);

	state.chapters.ids.map((chapterId) =>
		renumberChapterPages(state, state.chapters.entities[chapterId])
	);

	return state;
};

export const deleteChapterHelper = (state: CourseState, payload) => {
	const { chapterId } = payload;
	const initialChapterIds = state.initialCourse.chapters.ids;

	const chapter = state.chapters.entities[chapterId];
	const currentChapterIds = state.chapters.ids.filter((id) => id !== chapterId);

	// remove children
	if (chapter.page_groups_enabled) {
		chapter.page_groups.map((pageGroupId) => {
			deletePageGroupFromChapterHelper(state, { chapterId, pageGroupId });
		});
	} else {
		chapter.pages.map((pageId) => {
			deletePageFromChapterHelper(state, { chapterId, pageId });
		});
	}

	state.chapters.entities = deleteProperty(state.chapters.entities, chapterId);
	state.chapters.ids = currentChapterIds;

	const isNew = state.changes.chapters[chapterId]?.includes(ChangesStatus.New);

	state.changes.chapters[chapterId] = isNew ? [] : [ChangesStatus.Deleted];
	state.changes.toc = isEqualIdsArrays(initialChapterIds, currentChapterIds)
		? removeStatus(state.changes.toc, ChangesStatus.ChildrenReorder)
		: addStatus(state.changes.toc, ChangesStatus.ChildrenReorder);

	state.chapters.ids.map((chapterId) =>
		renumberChapterPages(state, state.chapters.entities[chapterId])
	);

	return state;
};

export const updateChapterHelper = (state: CourseState, payload) => {
	const { chapterId, ...chapter } = payload;

	const initialChapter = state.initialCourse.chapters.entities[chapterId];
	const updatedChapter = { ...state.chapters.entities[chapterId], ...chapter };

	const attributesEqual = initialChapter
		? isEqual(
				chapterToChapterProperties(initialChapter),
				chapterToChapterProperties(updatedChapter)
		  )
		: false;

	state.chapters.entities[chapterId] = updatedChapter;
	state.settingsMode = chapter.chapter_type ? !attributesEqual : state.settingsMode;

	state.changes.chapters[chapterId] = attributesEqual
		? removeStatus(state.changes.chapters[chapterId], ChangesStatus.Modified)
		: addStatus(state.changes.chapters[chapterId], ChangesStatus.Modified);

	return state;
};
